/**
 * Cart line-item identity for variant products.
 *
 * A cart/collection line is keyed by productId alone when the product has no
 * variant chosen (or has no variants at all) — this is exactly today's key
 * shape, so every pre-existing cart entry and every shared `/cart?ids=...`
 * link with no `~` in it keeps working unchanged. When a variant IS chosen,
 * the line key is `productId~variantId`.
 *
 * `~` is RFC-3986 unreserved, so it never needs URL-encoding inside the
 * `ids=` query param, and it can't collide with a UUID-shaped productId or
 * variantId.
 */
export const LINE_SEP = "~";

/** Build a cart line key. Omitting/undefined-ing variantId returns the bare
 *  productId — "the product itself", matching every pre-variant record. */
export function toLineKey(productId: string, variantId?: string | null): string {
  return variantId ? `${productId}${LINE_SEP}${variantId}` : productId;
}

export interface ParsedLineKey {
  productId: string;
  /** Absent when the line key is a bare productId (no variant chosen). */
  variantId?: string;
}

/** Inverse of toLineKey. A line key with no `~` parses as { productId } —
 *  old shared links and non-variant products both land here identically. */
export function parseLineKey(lineKey: string): ParsedLineKey {
  const idx = lineKey.indexOf(LINE_SEP);
  if (idx === -1) return { productId: lineKey };
  return {
    productId: lineKey.slice(0, idx),
    variantId: lineKey.slice(idx + LINE_SEP.length),
  };
}

const VariantLineKeyHelper = { LINE_SEP, toLineKey, parseLineKey };
export default VariantLineKeyHelper;
